import React from 'react';
import { Box, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import {
	atom_guideRoute,
	selector_isTrackerActive,
	selector_trackerRouteType,
	selector_trackerTargetStopId,
	selector_trackerCurrentGuideStepIndex,
	selector_trackerDistanceToTarget,
} from '@app/shared/state/route-tracker.atom';
import { atom_isNavigationMode, atom_tourState } from '@app/shared/state/tour.atom';

// Maps OpenRouteService step.type numbers to FontAwesome icon classes.
// Reference: https://github.com/GIScience/openrouteservice-docs#instruction-types
const TYPE_TO_ICON: Record<number, string> = {
	0: 'fa-solid fa-arrow-left',           // Turn left
	1: 'fa-solid fa-arrow-right',          // Turn right
	2: 'fa-solid fa-arrow-rotate-left',    // Sharp left
	3: 'fa-solid fa-arrow-rotate-right',   // Sharp right
	4: 'fa-solid fa-turn-up',              // Slight left
	5: 'fa-solid fa-turn-up',              // Slight right
	6: 'fa-solid fa-arrow-up',             // Straight
	7: 'fa-solid fa-rotate-right',         // Enter roundabout
	8: 'fa-solid fa-rotate-right',         // Exit roundabout
	9: 'fa-solid fa-arrow-rotate-right',   // U-turn
	10: 'fa-solid fa-flag-checkered',      // Goal
	11: 'fa-solid fa-arrow-up',            // Depart
	12: 'fa-solid fa-arrow-up-long',       // Keep left
	13: 'fa-solid fa-arrow-up-long',       // Keep right
};

function formatMeters(m: number | null | undefined): string {
	if (m == null || !isFinite(m)) return '—';
	if (m < 1000) return `${Math.round(m)}m`;
	return `${(m / 1000).toFixed(1)}km`;
}

export const GuideStepMiniPanel: React.FC = React.memo(function GuideStepMiniPanel() {
	const isNavMode = useRecoilValue(atom_isNavigationMode);
	const isActive = useRecoilValue(selector_isTrackerActive);
	const routeType = useRecoilValue(selector_trackerRouteType);
	const targetStopId = useRecoilValue(selector_trackerTargetStopId);
	const currentStepIndex = useRecoilValue(selector_trackerCurrentGuideStepIndex);
	const distanceToTarget = useRecoilValue(selector_trackerDistanceToTarget);
	const guideRoute = useRecoilValue(atom_guideRoute);
	const tourState = useRecoilValue(atom_tourState);

	// Gate: only show in guide-route nav mode
	if (!isActive || routeType !== 'guide-route' || !targetStopId || !guideRoute?.segments) {
		return null;
	}

	// Find the segment the user is navigating. Normal case: segment.toStopId === targetStopId.
	// Fallbacks handle stops that aren't segment endpoints (e.g. transportation type "none"):
	//   1. Segment going FROM the target (user hasn't reached first stop yet)
	//   2. Forward-walk: skip ahead in stopOrder until we find a stop that IS an endpoint
	let segment: any = guideRoute.segments.find((s: any) => s.toStopId === targetStopId);
	if (!segment) segment = guideRoute.segments.find((s: any) => s.fromStopId === targetStopId);
	if (!segment) {
		const idx = guideRoute.stopOrder.indexOf(targetStopId);
		if (idx >= 0) {
			for (let i = idx + 1; i < guideRoute.stopOrder.length; i++) {
				const id = guideRoute.stopOrder[i];
				segment = guideRoute.segments.find((s: any) => s.toStopId === id || s.fromStopId === id);
				if (segment) break;
			}
		}
	}
	if (!segment || !Array.isArray(segment.steps) || segment.steps.length === 0) return null;

	const steps: any[] = (segment as any).steps;
	const step = steps[Math.min(currentStepIndex, steps.length - 1)];
	if (!step) return null;

	const iconClass = TYPE_TO_ICON[step.type] || 'fa-solid fa-arrow-up';
	const instruction = step.instruction || '';
	const distanceToTurn = typeof step.distance === 'number' ? step.distance : null;

	// Destination stop name — the stop this segment ends at
	const destStop = tourState?.stops?.find((s: any) => s.id === segment.toStopId);
	const destName = destStop?.s_title || '';

	return (
		<Box
			sx={{
				position: 'fixed',
				bottom: '30%',
				left: 16,
				zIndex: 1200,
				bgcolor: 'rgba(255,255,255,0.98)',
				borderRadius: 2,
				px: 2,
				py: 1.25,
				display: 'flex',
				alignItems: 'center',
				gap: 1.5,
				boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
				minWidth: 240,
				maxWidth: 'calc(100vw - 32px)',
				pointerEvents: 'none',
			}}
			role="status"
			aria-live="polite"
		>
			<i className={iconClass} style={{ fontSize: 22, color: '#1F313B', flexShrink: 0 }} aria-hidden="true" />
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F313B', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{instruction}
				</Typography>
				{destName && (
					<Typography sx={{ fontSize: 12, color: '#48526B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.25 }}>
						→ {destName}
					</Typography>
				)}
				<Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
					<Typography sx={{ fontSize: 12, color: '#48526B' }}>
						Turn: {formatMeters(distanceToTurn)}
					</Typography>
					<Typography sx={{ fontSize: 12, color: '#48526B' }}>
						Stop: {formatMeters(distanceToTarget)}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
});
